import React, { useCallback, useEffect, useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import type { NumberStepperProps } from './NumberStepper.types'
import './NumberStepper.scss'

/**
 * **Der Zahlen-Stepper** — ein Zahlenfeld mit einer schmalen Pfeilspalte (Figma
 * `5372:178359`). Gebaut für den **Wiederkehr-Wähler** („Alle [3] Tage", „[3] mal pro
 * Woche"): Das native `type="number"` trägt weder das Kleid noch hält es die Grenzen
 * beim Tippen.
 *
 * **Er meldet nur gültige Zahlen.** Was getippt wird, steht als Text im Feld; gemeldet
 * wird erst, wenn daraus eine ganze Zahl in den Grenzen geworden ist. Ein leeres oder
 * halbes Feld ist ein Entwurf des Tippenden — der Aufrufer schreibt womöglich sofort
 * weiter (Aufgaben-Details, ADR 0026), und ein „0" unterwegs wäre eine ungültige Regel
 * an der Aufgabe. Beim Verlassen kehrt das Feld zum letzten gültigen Wert zurück.
 *
 * Es korrigiert NICHT unter den Fingern: Wer „9" in ein Feld bis 5 tippt, sieht „9"
 * stehen, bis er das Feld verlässt — ein Feld, das Getipptes sofort umschreibt, liest
 * sich als kaputt.
 *
 * @example
 * <NumberStepper value={rule.interval} onValueChange={setInterval} min={1} max={999}
 *   aria-label="Intervall" incrementLabel="Mehr" decrementLabel="Weniger" />
 *
 * @example
 * // Großer Schritt an den Knöpfen und mit ⇧, Getipptes erst mit Enter (Textgröße)
 * <NumberStepper value={size} onValueChange={preview} onValueCommit={apply}
 *   buttonStep={8} shiftStep={8} min={8} max={400}
 *   aria-label="Eigene Größe" incrementLabel="Größer" decrementLabel="Kleiner" />
 */
export const NumberStepper: React.FC<NumberStepperProps> = ({
  value, onValueChange, onValueCommit, buttonStep = 1, shiftStep = 1, min = 1, max = 999, disabled = false, arrows = true,
  'aria-label': ariaLabel, incrementLabel, decrementLabel, className, autoFocus = false,
}) => {
  const shown = value === null ? '' : String(value)
  const [text, setText] = useState(shown)

  // Ein Wert von außen (Pfeil, anderer Eintrag, andere Aufgabe) ersetzt den Text —
  // außer er IST schon, was dasteht: Sonst setzte jede eigene Meldung den Text neu
  // und risse den Caret ans Ende.
  useEffect(() => {
    setText(current => (value !== null && parseInt(current, 10) === value ? current : value === null ? '' : String(value)))
  }, [value])

  const clamp = useCallback((n: number) => Math.min(max, Math.max(min, n)), [min, max])

  const step = useCallback((delta: number) => {
    if (value === null) return
    const next = clamp(value + delta)
    if (next !== value) onValueChange(next)
  }, [value, clamp, onValueChange])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '')
    setText(digits)
    if (onValueCommit) return
    const parsed = parseInt(digits, 10)
    if (Number.isInteger(parsed) && parsed >= min && parsed <= max && parsed !== value) onValueChange(parsed)
  }, [min, max, value, onValueChange, onValueCommit])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onValueCommit) {
      const parsed = parseInt(text, 10)
      if (!Number.isInteger(parsed)) return
      e.preventDefault()
      onValueCommit(clamp(parsed))
      return
    }
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
    e.preventDefault()
    step((e.key === 'ArrowUp' ? 1 : -1) * (e.shiftKey ? shiftStep : 1))
  }, [step, shiftStep, onValueCommit, text, clamp])

  return (
    <div className={`number-stepper${arrows ? '' : ' number-stepper--plain'}${disabled ? ' number-stepper--disabled' : ''}${className ? ` ${className}` : ''}`}>
      <input
        className="number-stepper__field"
        type="text"
        inputMode="numeric"
        role="spinbutton"
        aria-label={ariaLabel}
        aria-valuenow={value ?? undefined}
        aria-valuemin={min}
        aria-valuemax={max}
        // So breit wie die Ziffern — ein dreistelliger Wert dehnt das Feld, statt abgeschnitten zu werden.
        size={Math.max(1, text.length)}
        value={text}
        disabled={disabled}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => setText(shown)}
        autoFocus={autoFocus}
        onFocus={autoFocus ? e => e.currentTarget.select() : undefined}
      />
      {arrows && <div className="number-stepper__arrows">
        <button
          type="button" className="number-stepper__arrow number-stepper__arrow--up"
          aria-label={incrementLabel} tabIndex={-1}
          disabled={disabled || value === null || value >= max} onClick={() => step(buttonStep)}
        >
          <ChevronUp size={16} />
        </button>
        <button
          type="button" className="number-stepper__arrow number-stepper__arrow--down"
          aria-label={decrementLabel} tabIndex={-1}
          disabled={disabled || value === null || value <= min} onClick={() => step(-buttonStep)}
        >
          <ChevronDown size={16} />
        </button>
      </div>}
    </div>
  )
}
